/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { ArrowRightIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import type { RefObject } from "react"
import { useId, useRef, useState } from "react"
import { Link } from "react-router"

import { ApplicationStatus } from "../../features/application/application-types"
import type { ApplicationType, ThreatOverview } from "../../features/application-type/application-type-types"
import { AppRoutes } from "../../routes/routes"
import { prepareUrl } from "../../utils/prepare-application-url"
import { randomBetween } from "../../utils/random-between"
import { useAnimateValueEffect } from "../../utils/use-animate-value-effect"
import { valueOrDash } from "../../utils/value-or-dash"
import { AppIcon } from "../components/app-icon"
import { CategoryPlatform } from "../components/category-platform"
import { Skeleton } from "../primitives/skeleton"
import { TableCell, TableRow } from "../primitives/table"

import styles from "./dashboard-view.module.css"

interface Props {
    /**
     * Application to display.
     * Missing text fields are shown as an em dash.
     */
    appType: ApplicationType
    /**
     * Device threat counts, or `null` while unavailable.
     * A failed request also leaves this `null`, so the count cells continue to show skeletons.
     */
    threat: ThreatOverview | null
}

/**
 * Renders an application as a table row with its icon, details, and device counts.
 *
 * In narrow dashboard containers, the icon is hidden and the category/platform
 * badge moves below the package name. The layout responds to the `dashboard`
 * container defined by `DashboardView`.
 */
export const DashboardRow = observer(({ appType, threat }: Props) => {
    const nameId = useId()
    const packageNameId = useId()

    const counts = threat && {
        insecure: threat.insecure ?? 0,
        outdated: (threat.outdatedIos ?? 0) + (threat.outdatedAndroid ?? 0),
        active: threat.active ?? 0
    }

    const countProps = { appTypeId: appType.id, describedBy: `${nameId} ${packageNameId}` }

    return (
        <TableRow>
            {/*
             * Let the details column fill the space left by the count columns,
             * truncating long text rather than expanding the table.
             *
             * Keep the icon, details, and badge in one table cell so hiding the
             * icon at narrow widths doesn't shift the count columns out of
             * alignment with their headers. The grid moves the badge below the
             * package name when there isn't room for a single row.
             */}
            <TableCell className="w-full max-w-0">
                <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 @max-xl/dashboard:grid-cols-1 @max-xl/dashboard:gap-y-1">
                    <AppIcon appTypeId={appType.id} className="@max-xl/dashboard:hidden" />
                    <div>
                        <div id={nameId} className="truncate text-base font-semibold">
                            {valueOrDash(appType.description)}
                        </div>
                        <div id={packageNameId} className="truncate text-muted-foreground">
                            {valueOrDash(appType.sourcePackageName)}
                        </div>
                    </div>
                    <CategoryPlatform category={appType.category} platform={appType.platform} />
                </div>
            </TableCell>
            <CountCell kind="insecure" count={counts?.insecure ?? null} {...countProps} />
            <CountCell kind="outdated" count={counts?.outdated ?? null} {...countProps} />
            <CountCell kind="active" count={counts?.active ?? null} {...countProps} />
        </TableRow>
    )
})

/**
 * Maps each count status to its device-list filter. Outdated devices have no
 * dedicated list, so that count is rendered as plain text rather than a link.
 *
 * The status keys also match the tone classes in `dashboard-view.module.css`,
 * shared with the dashboard tiles.
 */
const CountStatus = {
    insecure: ApplicationStatus.insecure,
    outdated: undefined,
    active: ApplicationStatus.active
} as const

/**
 * Uses English plural rules to label device counts.
 */
const pluralRules = new Intl.PluralRules("en")

interface CountCellProps {
    /**
     * Count type, used to select its label, styling, and destination.
     */
    kind: keyof typeof CountStatus
    /**
     * Device count, or `null` while unavailable.
     */
    count: number | null
    /**
     * The application whose device list a linked count opens.
     */
    appTypeId: number | undefined
    /**
     * IDs of the application's name and package name. The Android and iOS
     * builds often share a name, so the package tells the links apart.
     */
    describedBy: string
}

/**
 * Renders a device count, either as a link to the device list or as plain text.
 *
 * Animates the visible number without re-rendering the cell. Since the
 * animation may display a rounded or intermediate value, assistive technology
 * receives a separate label containing the exact count.
 */
function CountCell({ kind, count, appTypeId, describedBy }: CountCellProps) {
    const ref = useRef<HTMLSpanElement>(null)

    // Keep the duration stable so unrelated re-renders don't restart the count-up.
    const [duration] = useState(randomDuration)
    useAnimateValueEffect(ref, duration, count)

    const status = CountStatus[kind]
    const content = {
        ref,
        label: `${count} ${kind} ${count !== null && pluralRules.select(count) === "one" ? "device" : "devices"}`,
        color: count === 0 ? "text-muted-foreground" : "text-(--tone)"
    }

    return (
        // Match the narrow layout's right padding to the linked count's arrow spacing.
        // Tint the cell only while its link is hovered; a plain count has no link to hover.
        <TableCell
            className={clsx(
                "relative w-0 border-l px-3 text-right has-[a:hover]:bg-(--tone)/10 @max-xl/dashboard:px-2 @max-xl/dashboard:last:pe-2",
                styles[kind]
            )}
        >
            {count === null
                ? renderLoadingCount()
                : status
                  ? renderLinkedCount(content, prepareUrl(AppRoutes.Application, status, appTypeId), describedBy)
                  : renderPlainCount(content)}
        </TableCell>
    )
}

/**
 * Shared content for linked and plain-text counts.
 */
interface CountContent {
    /**
     * Span updated directly by `useAnimateValueEffect`.
     */
    ref: RefObject<HTMLSpanElement | null>
    /**
     * Exact count and device label announced by screen readers.
     */
    label: string
    /**
     * Count colour, muted when the value is zero.
     */
    color: string
}

/**
 * Renders a skeleton while the count is unavailable.
 *
 * The end padding reserves the same space as the arrow in a linked count,
 * keeping the digits aligned across cells and responsive layouts.
 */
function renderLoadingCount() {
    return (
        <div className="pe-5 @max-xl/dashboard:pe-4">
            <Skeleton aria-hidden className="ms-auto h-5 w-8" />
        </div>
    )
}

/**
 * Renders a count as a link to the application's device list.
 *
 * The link's pseudo-element covers the positioned table cell, making the
 * entire cell clickable without changing the link's inline layout. Its focus
 * ring is inset to stay within the cell borders.
 *
 * The arrow's space is reserved to prevent the count from shifting on hover.
 * It remains visible on narrow layouts and coarse pointers, where hover isn't
 * available.
 */
function renderLinkedCount({ ref, label, color }: CountContent, to: string, describedBy: string) {
    return (
        <Link
            to={to}
            aria-label={label}
            aria-describedby={describedBy}
            className={clsx(
                "group inline-flex items-center gap-1 text-xl font-bold tabular-nums no-underline outline-none after:absolute after:inset-0 focus-visible:after:inset-ring-3 focus-visible:after:inset-ring-ring/50 @max-xl/dashboard:gap-0.5 @max-xl/dashboard:text-lg",
                color
            )}
        >
            <span ref={ref} />
            <ArrowRightIcon className="size-4 opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 @max-xl/dashboard:size-3.5 @max-xl/dashboard:opacity-100 pointer-coarse:opacity-100" />
        </Link>
    )
}

/**
 * Renders a count as plain text when no device-list destination is available.
 *
 * Reserves the space used by the linked count's arrow to keep the digits
 * aligned. Screen readers receive the exact label instead of the animated
 * number, which is hidden from assistive technology.
 */
function renderPlainCount({ ref, label, color }: CountContent) {
    return (
        <span
            className={clsx(
                "pe-5 text-xl font-bold tabular-nums @max-xl/dashboard:pe-4 @max-xl/dashboard:text-lg",
                color
            )}
        >
            <span className="sr-only">{label}</span>
            <span ref={ref} aria-hidden />
        </span>
    )
}

/**
 * Returns a per-cell animation duration so counts don't all finish animating
 * at the same time.
 */
function randomDuration(): number {
    return randomBetween(1_000, 1_600)
}
